import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../api/axios";

/* ── Monday-first ordering; server weekday numbers are 0=Sunday .. 6=Saturday ── */
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const MINI_LABEL = {
  fontSize: 10, fontWeight: 700, color: "var(--mly-text-soft)",
  textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3,
};

const FIELD_INPUT = {
  width: "100%", padding: "7px 8px", borderRadius: 8,
  border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
  fontSize: 12.5, boxSizing: "border-box", outline: "none", color: "var(--mly-text)",
};

const BAR_LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-soft)", marginBottom: 6, textTransform: "uppercase",
};

const BAR_INPUT = {
  padding: "10px 12px", borderRadius: 10,
  border: "1.5px solid var(--mly-border)", background: "var(--mly-surface)",
  fontSize: 13.5, boxSizing: "border-box", outline: "none", color: "var(--mly-text)",
};

const focusForest = (e) => { e.target.style.borderColor = "var(--mly-accent)"; };
const blurSand    = (e) => { e.target.style.borderColor = "var(--mly-border)"; };

const emptyDay    = () => ({ status: true, windows: [] });
const emptyWindow = () => ({ startTime: "", endTime: "", capacityKg: "", leadMin: "90" });

const todayStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const WeeklySchedule = ({ millType, onGenerated }) => {
  const [days,       setDays]       = useState(null);   // { 0..6: { status, windows } }
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState("");
  const [dirtyDays,  setDirtyDays]  = useState({});
  const [savingDay,  setSavingDay]  = useState(null);


  /* ── emergency closures ── */
  const [closures,    setClosures]    = useState([]);
  const [closeDate,   setCloseDate]   = useState(todayStr());
  const [closeReason, setCloseReason] = useState("");
  const [closing,     setClosing]     = useState(false);

  const fetchClosures = useCallback(async () => {
    try {
      /* still enveloped: the handler answers { data: [...], storeId } */
      const res = await api.get(`/admin/milling/closures?millType=${millType}`);
      setClosures(res.data?.data || []);
    } catch { /* non-fatal */ }
  }, [millType]);

  useEffect(() => { fetchClosures(); }, [fetchClosures]);

  const handleClose = async () => {
    if (!closeDate) return;
    setClosing(true);
    try {
      /* `date` is still the REQUEST field name — only the response row renamed
         it to closedOn */
      const res = await api.post("/admin/milling/closures", {
        millType, date: closeDate, reason: closeReason.trim(),
      });
      toast.success(res.data?.message || "Day closed");
      setCloseReason("");
      fetchClosures();
      if (onGenerated) onGenerated(); // slot list changed (disabled)
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't close that day");
    } finally { setClosing(false); }
  };

  const handleReopen = async (id) => {
    try {
      /* {id:long}: the closure id is a bigint now, and a 24-hex id would match
         no route at all — a bodiless 404, i.e. a toast with nothing in it */
      const res = await api.delete(`/admin/milling/closures/${id}`);
      toast.success(res.data?.message || "Day reopened");
      /* reopening re-enables EVERY disabled window on that day, not only the
         ones this closure switched off — a window disabled by hand comes back
         with them, so the server says so and the operator should hear it */
      if (res.data?.note) toast.info(res.data.note);
      fetchClosures();
      if (onGenerated) onGenerated();
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't reopen that day");
    }
  };

  const fetchSchedule = useCallback(async () => {
    setLoading(true); setLoadError("");
    try {
      /* still enveloped: the handler answers { data: [...], storeId } */
      const res = await api.get(`/admin/milling/schedule?millType=${millType}`);
      const templates = res.data?.data || [];
      const next = {};
      for (let wd = 0; wd < 7; wd++) next[wd] = emptyDay();
      templates.forEach((t) => {
        if (next[t.weekday] === undefined) return;
        next[t.weekday] = {
          status: t.isActive !== false,
          windows: (t.windows || []).map((w) => ({
            startTime: w.startTime || "",
            endTime: w.endTime || "",
            capacityKg: w.capacityGrams != null ? String(+(w.capacityGrams / 1000).toFixed(2)) : "",
            leadMin: String(w.leadMinutes ?? 90),
          })),
        };
      });
      setDays(next);
      setDirtyDays({});
    } catch (err) {
      setLoadError(err.response?.data?.message || "Couldn't load the weekly schedule");
    } finally {
      setLoading(false);
    }
  }, [millType]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  /* ── local edits (saved per day via the Save button) ── */
  const markDirty = (wd) => setDirtyDays((prev) => ({ ...prev, [wd]: true }));

  const setDayStatus = (wd, status) => {
    setDays((prev) => ({ ...prev, [wd]: { ...prev[wd], status } }));
    markDirty(wd);
  };

  const updateWindow = (wd, idx, field, value) => {
    setDays((prev) => ({
      ...prev,
      [wd]: {
        ...prev[wd],
        windows: prev[wd].windows.map((w, i) => (i === idx ? { ...w, [field]: value } : w)),
      },
    }));
    markDirty(wd);
  };

  const addWindow = (wd) => {
    setDays((prev) => ({ ...prev, [wd]: { ...prev[wd], windows: [...prev[wd].windows, emptyWindow()] } }));
    markDirty(wd);
  };

  const removeWindow = (wd, idx) => {
    setDays((prev) => ({ ...prev, [wd]: { ...prev[wd], windows: prev[wd].windows.filter((_, i) => i !== idx) } }));
    markDirty(wd);
  };

  /* ── per-day save ── */
  const validateDay = (wd, day) => {
    for (let i = 0; i < day.windows.length; i++) {
      const w = day.windows[i];
      const at = `${DAY_NAMES[wd]}, window ${i + 1}`;
      if (!w.startTime || !w.endTime) return `${at}: start and end times are required`;
      if (w.endTime <= w.startTime)   return `${at}: end time must be after start time`;
      if (w.capacityKg === "" || isNaN(w.capacityKg) || Number(w.capacityKg) <= 0) return `${at}: valid capacity (kg) is required`;
      if (w.leadMin === "" || isNaN(w.leadMin) || Number(w.leadMin) < 0)           return `${at}: valid lead minutes is required`;
    }
    return null;
  };

  const saveDay = async (wd) => {
    const day = days[wd];
    const invalid = validateDay(wd, day);
    if (invalid) { toast.error(invalid); return; }

    setSavingDay(wd);
    try {
      /* the request keeps the portal's spelling: ScheduleSave accepts `status`
         and WindowInput accepts `millingLeadMinutes`; only the reads renamed */
      const res = await api.put(`/admin/milling/schedule/${wd}`, {
        millType,
        status: !!day.status,
        windows: day.windows.map((w) => ({
          startTime: w.startTime,
          endTime: w.endTime,
          capacityGrams: Math.round(Number(w.capacityKg) * 1000),
          millingLeadMinutes: Number(w.leadMin),
        })),
      });
      toast.success(res.data?.message || `${DAY_NAMES[wd]} schedule saved`);
      setDirtyDays((prev) => {
        const next = { ...prev };
        delete next[wd];
        return next;
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't save the day schedule");
    } finally {
      setSavingDay(null);
    }
  };

  return (
    <div style={{
      background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14,
      marginBottom: 24, overflow: "hidden",
    }}>
      {/* ── header ── */}
      <div style={{
        padding: "16px 20px", borderBottom: "1px solid var(--mly-border)",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "var(--mly-heading)" }}>
            Weekly Schedule
          </h3>
          <div style={{ fontSize: 12, color: "var(--mly-text-soft)", marginTop: 2 }}>
            Recurring windows for the {millType === "masala" ? "masala" : "flour"} mill — edit a day, save it, then generate slots
          </div>
        </div>
        <span style={{
          background: "var(--mly-accent-soft)", color: "var(--mly-heading)",
          fontSize: 11, fontWeight: 700, letterSpacing: 1, padding: "4px 12px",
          borderRadius: 20, border: "1px solid var(--mly-heading)", textTransform: "uppercase",
        }}>
          {millType === "masala" ? "Masala Mill" : "Flour Mill"}
        </span>
      </div>

      <div style={{ padding: 20 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "36px 0", color: "var(--mly-text-soft)", fontSize: 13.5 }}>
            Loading weekly schedule...
          </div>
        ) : loadError ? (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
            padding: "18px 20px", display: "flex", alignItems: "center",
            justifyContent: "space-between", flexWrap: "wrap", gap: 10,
          }}>
            <span style={{ color: "#dc2626", fontSize: 13.5, fontWeight: 600 }}>{loadError}</span>
            <button onClick={fetchSchedule} style={{
              padding: "8px 18px", borderRadius: 8, border: "1.5px solid #dc2626",
              background: "#fff", color: "#dc2626", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>
              Retry
            </button>
          </div>
        ) : days && (
          <>
            {/* ── 7-day grid, Monday first ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
              {WEEKDAY_ORDER.map((wd) => {
                const day = days[wd];
                const isSaving = savingDay === wd;
                const isDirty  = !!dirtyDays[wd];
                return (
                  <div key={wd} style={{
                    background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
                    borderRadius: 12, padding: 14,
                    display: "flex", flexDirection: "column", gap: 10,
                    opacity: day.status ? 1 : 0.72, transition: "opacity 0.15s",
                  }}>
                    {/* day header */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: 800, fontSize: 14, color: "var(--mly-heading)" }}>
                        {DAY_NAMES[wd]}
                      </span>
                      <label className="switch" style={{ margin: 0 }} title={day.status ? "Day is active" : "Day is off"}>
                        <input type="checkbox" checked={!!day.status} onChange={() => setDayStatus(wd, !day.status)} />
                        <span className="slider round" />
                      </label>
                    </div>

                    {/* windows */}
                    {day.windows.length === 0 ? (
                      <div style={{
                        background: "var(--mly-surface)", border: "1px dashed var(--mly-border-soft)", borderRadius: 10,
                        padding: "14px 10px", textAlign: "center",
                        fontSize: 12, fontStyle: "italic", color: "var(--mly-text-soft)",
                      }}>
                        Mill closed — no windows
                      </div>
                    ) : (
                      day.windows.map((w, idx) => (
                        <div key={idx} style={{
                          background: "var(--mly-surface)", border: "1px solid var(--mly-border)",
                          borderRadius: 10, padding: 10,
                        }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 26px", gap: 6, alignItems: "end" }}>
                            <div>
                              <div style={MINI_LABEL}>Start</div>
                              <input type="time" step="900" value={w.startTime}
                                onChange={(e) => updateWindow(wd, idx, "startTime", e.target.value)}
                                style={FIELD_INPUT} onFocus={focusForest} onBlur={blurSand}
                              />
                            </div>
                            <div>
                              <div style={MINI_LABEL}>End</div>
                              <input type="time" step="900" value={w.endTime}
                                onChange={(e) => updateWindow(wd, idx, "endTime", e.target.value)}
                                style={FIELD_INPUT} onFocus={focusForest} onBlur={blurSand}
                              />
                            </div>
                            <button onClick={() => removeWindow(wd, idx)} title="Remove window"
                              style={{
                                width: 26, height: 30, borderRadius: 7, border: "none",
                                background: "#fff5f5", color: "#ef4444", cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 15, fontWeight: 700, lineHeight: 1,
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff5f5"; }}
                            >
                              ×
                            </button>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
                            <div>
                              <div style={MINI_LABEL}>Capacity (kg)</div>
                              <input type="number" value={w.capacityKg} placeholder="e.g. 50" min="0" step="0.5"
                                onChange={(e) => updateWindow(wd, idx, "capacityKg", e.target.value)}
                                style={FIELD_INPUT} onFocus={focusForest} onBlur={blurSand}
                              />
                            </div>
                            <div>
                              <div style={MINI_LABEL}>Lead (min)</div>
                              <input type="number" value={w.leadMin} placeholder="90" min="0" step="5"
                                onChange={(e) => updateWindow(wd, idx, "leadMin", e.target.value)}
                                style={FIELD_INPUT} onFocus={focusForest} onBlur={blurSand}
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}

                    {/* add window (ghost) */}
                    <button onClick={() => addWindow(wd)}
                      style={{
                        padding: "7px 0", borderRadius: 8,
                        border: "1.5px dashed var(--mly-heading)", background: "transparent",
                        color: "var(--mly-heading)", fontWeight: 700, fontSize: 12, cursor: "pointer",
                      }}
                    >
                      + Add window
                    </button>

                    {/* per-day save */}
                    <button onClick={() => saveDay(wd)} disabled={isSaving}
                      style={{
                        marginTop: "auto", padding: "8px 0", borderRadius: 8, border: "none",
                        background: isSaving
                          ? "var(--mly-accent)"
                          : isDirty ? "var(--mly-heading)" : "var(--mly-accent-soft)",
                        color: isDirty || isSaving ? "var(--mly-on-accent)" : "var(--mly-heading)",
                        fontWeight: 700, fontSize: 12.5,
                        cursor: isSaving ? "not-allowed" : "pointer", transition: "all 0.15s",
                      }}
                    >
                      {isSaving ? "Saving..." : isDirty ? "Save changes" : "Save"}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* slots materialize automatically — on save, hourly on demand,
                and daily on the server; no manual generation needed */}
            <div style={{
              marginTop: 18, padding: "12px 16px", borderRadius: 12,
              background: "var(--mly-accent-soft)", border: "1px solid var(--mly-border)",
              fontSize: 12.5, color: "var(--mly-text-soft)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 15 }}>🗓️</span>
              Bookable slots for the next 7 days are created automatically from this
              schedule — the moment you save a day, and refreshed daily. Closures below
              override the pattern.
            </div>

            {/* ── emergency closures ── */}
            <div style={{
              marginTop: 14, padding: "14px 16px", borderRadius: 12,
              background: "var(--mly-surface-2)", border: "1px solid #F5D5D0",
            }}>
              <div style={{ ...BAR_LABEL, color: "#B4483B", marginBottom: 10 }}>
                Emergency closures — {millType} mill
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <label style={BAR_LABEL}>Date</label>
                  <input type="date" value={closeDate} min={todayStr()}
                    onChange={(e) => setCloseDate(e.target.value)}
                    style={BAR_INPUT} onFocus={focusForest} onBlur={blurSand} />
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label style={BAR_LABEL}>Reason (optional)</label>
                  <input type="text" value={closeReason} placeholder="e.g. maintenance, festival"
                    onChange={(e) => setCloseReason(e.target.value)}
                    style={{ ...BAR_INPUT, width: "100%" }} onFocus={focusForest} onBlur={blurSand} />
                </div>
                <button onClick={handleClose} disabled={closing}
                  style={{
                    padding: "11px 22px", borderRadius: 10, border: "none",
                    background: closing ? "#e8b3ac" : "#C0392B",
                    color: "#fff", fontWeight: 800, fontSize: 13,
                    cursor: closing ? "not-allowed" : "pointer",
                  }}>
                  {closing ? "Closing..." : "Close this day"}
                </button>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--mly-text-soft)", marginTop: 8 }}>
                Closing a day disables its slots and stops the generator from recreating them.
                Days with booked orders can&apos;t be closed until those orders are handled.
              </div>
              {closures.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                  {closures.map((c) => (
                    <span key={c.id} style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "6px 8px 6px 12px", borderRadius: 999,
                      background: "var(--mly-surface)", border: "1px solid #F5D5D0",
                      fontSize: 12.5, fontWeight: 600, color: "#B4483B",
                    }}>
                      {c.closedOn}{c.reason ? ` · ${c.reason}` : ""}
                      <button onClick={() => handleReopen(c.id)} title="Reopen this day"
                        style={{
                          border: "none", background: "#FDECEA", color: "#B4483B",
                          borderRadius: 999, width: 20, height: 20, cursor: "pointer",
                          fontWeight: 800, lineHeight: 1,
                        }}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WeeklySchedule;
